import { useMutation } from '@apollo/client';
import {
  AnalyticIdentifier,
  OrganizationInvitationError,
  OrganizationInvitationErrorSchema,
  getExtendedErrors,
} from '@common/neo.tax';
import * as Sentry from '@sentry/react';
import { Logo, ControlledInput, Typography, Loading, Stack } from '@ui-components/neo.tax';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { LoginRootContainer, StyledGeneralFormError, SubmitButton } from '@neo.tax/onboarding/organisms';
import {
  useUrlQueryParam,
  useCurrentUser,
  ONBOARD_USER,
  OnboardUserMutation,
  OnboardUserMutationVariables,
  validateFullName,
  validateIfExist,
  ALLOW_REAL_COMPANY,
  AllowRealCompanyMutation,
  AllowRealCompanyMutationVariables,
  RESEND_MY_ORGANIZATION_INVITATION,
  ResendMyOrganizationInvitationMutation,
  ResendMyOrganizationInvitationMutationVariables,
  useAnalyticId,
  NavLinkTo,
} from '@neo.tax/shared';
import { getPath, Path } from '@neo.tax/shared/utils';

import { useProfileFormIds } from './hooks';
import { ProfileContainer, ProfileHeader, StyledForm, LogoutButton } from './profile-form.styled';
import { ProfileFormValues } from './profile-form.types';

const SOMETHING_WENT_WRONG_ERROR = 'Something went wrong. Please try again.';

export const ProfileForm = (): React.ReactElement => {
  const logoutButtonId = useAnalyticId(AnalyticIdentifier.PROFILE_PAGE_LOGOUT_BUTTON);
  const { companyNameInputId, completeProfileId, fullNameInputId } = useProfileFormIds();

  const fullName = useUrlQueryParam('full_name');
  const firmName = useUrlQueryParam('firm_name');

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { loading: loadingUser, user } = useCurrentUser();

  const [onboardUser] = useMutation<OnboardUserMutation, OnboardUserMutationVariables>(ONBOARD_USER);
  const [allowRealCompany] = useMutation<AllowRealCompanyMutation, AllowRealCompanyMutationVariables>(
    ALLOW_REAL_COMPANY,
    {
      context: {
        errorToast: {
          title: 'Error allowing real compeny',
          description: 'Something unexpected prevented your changes from saving. Please try again.',
        },
      },
    },
  );

  const [resendMyOrganizationInvitation, { loading: resendLoading }] = useMutation<
    ResendMyOrganizationInvitationMutation,
    ResendMyOrganizationInvitationMutationVariables
  >(RESEND_MY_ORGANIZATION_INVITATION);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendSucceeded, setResendSucceeded] = useState<boolean>(false);

  const { control, formState, handleSubmit } = useForm<ProfileFormValues>({
    mode: 'onChange',
    defaultValues: {
      fullName: fullName || '',
      companyName: firmName || '',
    },
  });

  const onSubmit = async ({ companyName, fullName }: ProfileFormValues): Promise<void> => {
    setIsSubmitted(true);
    try {
      if (user) {
        await onboardUser({
          variables: {
            userId: user.id,
            fullName: user?.fullName || fullName,
            companyName,
          },
        });
      }
    } catch {
      setErrorMessage(SOMETHING_WENT_WRONG_ERROR);
      setIsSubmitted(false);
    }
  };

  const handleLogoClick = (e: MouseEvent): void => {
    // IF the event is NOT a triple-click THEN do nothing
    if (e.detail !== 3) {
      return;
    }

    // OTHERWISE set the user's allowRealCompanyDatetime
    if (user) {
      allowRealCompany({
        variables: {
          userId: user.id,
        },
      });
    }
  };

  // Situation (b): a user who signed up directly with an already-invited email lands here after
  // verifying. Surface their pending invite so they can finish joining instead of hitting the
  // generic "talk to an expert" dead-end. An EXPIRED invite can't be self-served (renewing is
  // admin-only), so it points them at their admin; a REVOKED invite is intentionally dead, so it
  // falls through to the default page.
  const pendingInvitation = user?.pendingOrFailedStatusOrganizationInvitations.find(
    (invitation) => invitation.derivedState === 'PENDING',
  );
  const expiredInvitation = user?.pendingOrFailedStatusOrganizationInvitations.find(
    (invitation) => invitation.derivedState === 'EXPIRED',
  );

  const onResendInvite = async (): Promise<void> => {
    if (!pendingInvitation) {
      return;
    }
    setResendError(null);
    try {
      await resendMyOrganizationInvitation({ variables: { organizationInvitationId: pendingInvitation.id } });
      setResendSucceeded(true);
    } catch (e) {
      // Surface backend-coded invitation errors verbatim (e.g. the 60s resend rate limit); page
      // Sentry only for genuinely unexpected failures. Mirrors ResendInvitationModal.
      let invitationErrors: OrganizationInvitationError[] = [];
      try {
        invitationErrors = getExtendedErrors(e, OrganizationInvitationErrorSchema);
      } catch {
        // Not an invitation-coded error.
      }
      if (invitationErrors.length > 0) {
        setResendError(invitationErrors[0].description);
      } else {
        setResendError(SOMETHING_WENT_WRONG_ERROR);
        Sentry.captureException(e);
      }
    }
  };

  if (loadingUser) {
    return (
      <LoginRootContainer>
        <Loading height={40} />;
      </LoginRootContainer>
    );
  }

  if (!user?.allowRealCompanyDatetime) {
    return (
      <LoginRootContainer>
        <ProfileContainer direction="column">
          <ProfileHeader direction="column">
            <Stack onClick={handleLogoClick}>
              {/* e2e tests depend on the id on Logo; don't remove it! */}
              <Logo id="neotax-logo" />
            </Stack>
            {pendingInvitation && (
              <>
                <Typography as="h2" fontSize="3xl" fontWeight="extrabold">
                  You&apos;ve been invited
                </Typography>
                <Typography as="p" fontSize="lg" fontWeight="normal">
                  Check your email inbox for the &quot;You&apos;ve been invited to Neo.Tax&quot; email and click the
                  Accept Invite button to finish joining your company.
                </Typography>
                {resendSucceeded ? (
                  <Typography as="p" fontSize="base" fontWeight="medium">
                    Invitation re-sent — check your inbox.
                  </Typography>
                ) : (
                  <SubmitButton
                    type="button"
                    size="base"
                    variant="primary"
                    rounded="regular"
                    disabled={resendLoading}
                    onClick={(): void => void onResendInvite()}
                  >
                    {resendLoading ? 'Resending...' : 'Resend invite'}
                  </SubmitButton>
                )}
                <StyledGeneralFormError error={!!resendError}>{resendError}</StyledGeneralFormError>
              </>
            )}
            {!pendingInvitation && expiredInvitation && (
              <>
                <Typography as="h2" fontSize="3xl" fontWeight="extrabold">
                  Your invitation has expired
                </Typography>
                <Typography as="p" fontSize="lg" fontWeight="normal">
                  Please ask your organization administrator to resend your invitation.
                </Typography>
              </>
            )}
            {!pendingInvitation && !expiredInvitation && (
              <>
                <Typography as="h2" fontSize="3xl" fontWeight="extrabold">
                  Let&apos;s Get Started
                </Typography>
                <Typography as="p" fontSize="lg" fontWeight="normal">
                  Talk with our team to start claiming your R&D credit.
                </Typography>
                <SubmitButton
                  as="a"
                  size="base"
                  variant="primary"
                  rounded="regular"
                  href="https://www.neo.tax/talk-to-an-expert"
                >
                  Get Started
                </SubmitButton>
              </>
            )}
          </ProfileHeader>
        </ProfileContainer>
        <LogoutButton
          size="sm"
          variant="white"
          rounded="regular"
          id={logoutButtonId}
          as={NavLinkTo}
          to={getPath({ path: Path.LOGOUT })}
        >
          Logout
        </LogoutButton>
      </LoginRootContainer>
    );
  }

  return (
    <LoginRootContainer>
      <ProfileContainer direction="column">
        <ProfileHeader direction="column">
          <Logo />
          <Typography as="h2" fontSize="3xl" fontWeight="extrabold">
            Let&apos;s Create Your Profile
          </Typography>
        </ProfileHeader>
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          {user?.fullName ? null : (
            <ControlledInput
              control={control}
              name="fullName"
              rules={{ required: true, validate: validateFullName }}
              id={fullNameInputId}
              label="Full Name"
              placeholder="Full Name"
            />
          )}
          <ControlledInput
            control={control}
            name="companyName"
            rules={{ required: true, validate: validateIfExist }}
            id={companyNameInputId}
            label="Organization Name"
            placeholder="Organization Name"
          />
          <SubmitButton
            type="submit"
            size="base"
            variant="primary"
            rounded="regular"
            disabled={!formState.isValid || isSubmitted}
            id={completeProfileId}
          >
            Let&apos;s Go!
          </SubmitButton>
          <StyledGeneralFormError error={!!errorMessage}>{errorMessage}</StyledGeneralFormError>
        </StyledForm>
      </ProfileContainer>
      <LogoutButton
        size="sm"
        variant="white"
        rounded="regular"
        id={logoutButtonId}
        as={NavLinkTo}
        to={getPath({ path: Path.LOGOUT })}
      >
        Logout
      </LogoutButton>
    </LoginRootContainer>
  );
};
